import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import { AlertTriangle, ChevronLeft, Loader2, Map as MapIcon, RefreshCw } from 'lucide-react';
import { MapFeature } from '../../../services/mapFeatures';
import { leadStatusFarbe, leadStatusLabel } from '../../../services/leadLogic';
import {
    CRM_MAP_LAYERS,
    CrmMapLayer,
    GeoCollection,
    LAYER_INFO,
    regionName,
    useCrmMap,
} from '../../../hooks/useCrmMap';
import {
    ClusterBounds,
    ClusterPunkt,
    CrmMapMetrik,
    METRIK_LABELS,
    RegionAggregate,
    choroplethFarbe,
    clusterBlaetter,
    clusterExpansionZoom,
    clusterFeatures,
    fristTage,
    istFrischerFund,
    metrikWert,
} from '../../../services/crmMapData';
import { CrmMapDetailPanel, CrmMapKpiPanel } from './CrmMapPanels';
import { CrmMapLegend } from './CrmMapLegend';
import { useVertriebRechte } from '../useVertriebRechte';

// Vollflaechige CRM-Deutschlandkarte mit schwebenden Glas-Panels
// (ERP/CRM-Plan Abschnitte 3.2 und 6).
//
// Aufteilung: dieser Container haelt AUSSCHLIESSLICH die Leaflet-Instanz und
// den Ausschnitt. Zustand und Daten liegen in useCrmMap, alle Ableitungen pur
// in crmMapData.ts, die Panels sind praesentational.
//
// GlobalMapDashboard.tsx bleibt unangetastet (imperativer Hotspot, Plan 3.2).
// Was hier bewusst anders laeuft als dort:
//   • Die Karte wird EINMAL erzeugt und nie wegen eines Filters neu gebaut.
//   • Marker liegen in EINER LayerGroup, die gezielt geleert wird — nicht in
//     einem Array, das man Marker fuer Marker abraeumt.
//   • Haversine wird nicht erneut lokal ausgeschrieben.
//   • Der Cluster-Index haengt allein an den Daten (useMemo im Hook); pro
//     Kartenbewegung wird nur abgefragt (Befund 5.2).

// ─── Kacheln ────────────────────────────────────────────────────────────────

/**
 * Kartenkacheln. Default ist der im Repo bereits genutzte OSM-Weg
 * (`osmMaps.ts` stitcht die statischen Karten aus genau diesen Kacheln) —
 * keyless, ohne Proxy, Namensnennung in der Legende.
 *
 * Die im Plan (3.2) erwogene CARTO-Basemap (Positron hell / Dark Matter
 * dunkel) waere ruhiger und wuerde die Flaechen-Einfaerbung besser zur Geltung
 * bringen. Sie ist bewusst NICHT aktiviert: der Betriebsentscheid dazu steht
 * aus (Nutzungsbedingungen, Quota, und vor allem der Datenschutzpunkt aus
 * Befund 5.4 — die Browser-IP jedes Nutzers ginge an einen Drittanbieter).
 * Zum Umschalten genuegt es, diese Konstante zu ersetzen:
 *   https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png
 *   https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png
 * Attribution dann zusaetzlich „© CARTO".
 */
const TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';

/** Deutschland-Ausschnitt beim Oeffnen (wie GlobalMapDashboard). */
const START_MITTE: [number, number] = [51.1657, 10.4515];
const START_ZOOM = 6;

// ─── Markerfarben ───────────────────────────────────────────────────────────

const KIND_FARBE: Record<string, string> = {
    customer: '#10b981', // emerald-500 — „gewonnene Kunden" (Plan, Layer-Tabelle)
    project: '#3b82f6',  // blue-500
    tender: '#f43f5e',   // rose-500
    freelancer: '#8b5cf6', // violet-500 (Plan, Layer-Tabelle)
};

const farbeFuer = (f: MapFeature): string =>
    f.kind === 'lead' ? leadStatusFarbe(f.status) : (KIND_FARBE[f.kind] ?? '#64748b');

/** HTML-Text sicher einbetten. DivIcons sind rohes HTML — hier kommen Namen rein. */
const esc = (s: unknown): string =>
    String(s ?? '').replace(/[&<>"']/g, (c) =>
        ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string,
    );

// ─── Symbole ────────────────────────────────────────────────────────────────

const punktIcon = (f: MapFeature, pulsiert: boolean): L.DivIcon => {
    const farbe = farbeFuer(f);
    const puls = pulsiert ? '<span class="crm-map-puls" style="--puls-farbe:' + farbe + '"></span>' : '';

    if (f.kind === 'tender') {
        // Fadenkreuz + Frist-Countdown (Plan, Layer-Tabelle „Ausschreibungs-Radar").
        const tage = fristTage(String(f.meta?.abgabefrist ?? ''));
        const badge =
            tage === null
                ? ''
                : `<span class="crm-map-frist ${tage < 0 ? 'crm-map-frist-ab' : tage <= 7 ? 'crm-map-frist-eng' : ''}">${
                      tage < 0 ? 'ab' : `${tage}T`
                  }</span>`;
        return L.divIcon({
            className: 'crm-map-icon',
            html: `<span class="crm-map-wrap">
                <svg width="26" height="26" viewBox="0 0 26 26" aria-hidden="true">
                  <circle cx="13" cy="13" r="9" fill="none" stroke="${farbe}" stroke-width="2"/>
                  <circle cx="13" cy="13" r="2.5" fill="${farbe}"/>
                  <path d="M13 1v5M13 20v5M1 13h5M20 13h5" stroke="${farbe}" stroke-width="2" stroke-linecap="round"/>
                </svg>${badge}</span>`,
            iconSize: [26, 26],
            iconAnchor: [13, 13],
        });
    }

    if (f.kind === 'freelancer') {
        // Personen-Symbol statt Punkt: der Marker steht fuer einen MENSCHEN mit
        // einem Aktionsradius, nicht fuer einen Ort. Der Kreis kommt separat
        // dazu (L.circle, s. Marker-Effekt weiter unten).
        return L.divIcon({
            className: 'crm-map-icon',
            html: `<span class="crm-map-wrap"><svg width="22" height="22" viewBox="0 0 22 22" aria-hidden="true">
                <circle cx="11" cy="11" r="9.5" fill="${farbe}" stroke="#fff" stroke-width="2"/>
                <circle cx="11" cy="8.4" r="2.9" fill="#fff"/>
                <path d="M5.6 17.2c0-3 2.4-5 5.4-5s5.4 2 5.4 5" fill="#fff"/>
              </svg></span>`,
            iconSize: [22, 22],
            iconAnchor: [11, 11],
        });
    }

    if (f.kind === 'project') {
        return L.divIcon({
            className: 'crm-map-icon',
            html: `<span class="crm-map-wrap"><svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
                <rect x="3" y="3" width="14" height="14" rx="3" fill="${farbe}" stroke="#fff" stroke-width="2"/>
              </svg></span>`,
            iconSize: [20, 20],
            iconAnchor: [10, 10],
        });
    }

    return L.divIcon({
        className: 'crm-map-icon',
        html: `<span class="crm-map-wrap">${puls}<svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
            <circle cx="10" cy="10" r="7" fill="${farbe}" stroke="#fff" stroke-width="2.5"/>
          </svg></span>`,
        iconSize: [20, 20],
        iconAnchor: [10, 10],
    });
};

const clusterIcon = (p: ClusterPunkt): L.DivIcon => {
    // Groesse waechst logarithmisch: linear wuerde ein 2.000er-Cluster den
    // halben Bildschirm bedecken.
    const groesse = Math.round(28 + Math.min(22, Math.log2(Math.max(2, p.anzahl)) * 4));
    // Farbe der haeufigsten Ebene im Cluster — so bleibt sichtbar, WORAUS die
    // Haeufung besteht, statt nur DASS es eine gibt.
    const dominant = Object.entries(p.kinds || {}).sort((a, b) => b[1] - a[1])[0]?.[0] ?? 'customer';
    const farbe = dominant === 'lead' ? '#60a5fa' : (KIND_FARBE[dominant] ?? '#64748b');
    const beschriftung = p.anzahl > 999 ? `${Math.round(p.anzahl / 1000)}k` : String(p.anzahl);
    return L.divIcon({
        className: 'crm-map-icon',
        html: `<span class="crm-map-cluster" style="width:${groesse}px;height:${groesse}px;background:${farbe}">${beschriftung}</span>`,
        iconSize: [groesse, groesse],
        iconAnchor: [groesse / 2, groesse / 2],
    });
};

const tooltipHtml = (f: MapFeature): string => {
    const zeilen: string[] = [`<strong>${esc(f.name)}</strong>`];
    if (f.kind === 'lead') zeilen.push(esc(leadStatusLabel(f.status)));
    if (f.meta?.auftraggeber) zeilen.push(esc(f.meta.auftraggeber));
    if (f.meta?.zip || f.meta?.city) zeilen.push(esc([f.meta?.zip, f.meta?.city].filter(Boolean).join(' ')));
    if (f.meta?.projectNumber) zeilen.push(`#${esc(f.meta.projectNumber)}`);
    return `<div class="text-[11px] leading-snug">${zeilen.join('<br/>')}</div>`;
};

// ─── Komponente ─────────────────────────────────────────────────────────────

interface Props {
    /** Sprung in die Kundenakte (App reicht die View-Umschaltung durch). */
    onOpenCustomer?: (customerId: string) => void;
    /** Sprung ins Projekt. */
    onOpenProject?: (projectId: string) => void;
}

export const CrmMapView: React.FC<Props> = ({ onOpenCustomer, onOpenProject }) => {
    // Rollensystem: „Kundenakte oeffnen“ nur, wenn die Wache in App.tsx die
    // Kundenakte oeffnet — sonst fuehrte der Knopf auf „Kein Zugriff“.
    const kundenakteOffen = useVertriebRechte().ansicht('crm');
    const karte = useCrmMap();
    const {
        layer, toggleLayer, metrik, setMetrik, monate, setMonate, zeitraum,
        drilldown, zeigeBund, zeigeLand, zeigeKreis,
        regionen, regionNachKey, skala, weisseFlecken, branchen,
        brancheFilter, setBrancheFilter, clusterIndex, sichtbareFeatures,
        geometrien, geometrienLaden, laedt, fehler, aggregateFehlen, neuLaden,
    } = karte;

    const containerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<L.Map | null>(null);
    const markerLayerRef = useRef<L.LayerGroup | null>(null);
    const flaechenLayerRef = useRef<L.GeoJSON | null>(null);

    const [viewport, setViewport] = useState<{ bounds: ClusterBounds; zoom: number } | null>(null);
    const [gewaehltesFeature, setGewaehltesFeature] = useState<MapFeature | null>(null);
    const [gewaehlteRegion, setGewaehlteRegion] = useState<RegionAggregate | null>(null);
    const [clusterListe, setClusterListe] = useState<MapFeature[]>([]);

    // Klartextnamen zu den amtlichen Schluesseln. Die Zuordnung waechst im
    // Modul-Scope des Hooks mit jeder geladenen Geometrieebene (siehe dort);
    // `geometrien` als Abhaengigkeit sorgt fuer eine neue Identitaet genau
    // dann, wenn Namen dazugekommen sein koennen.
    const nameFuer = useCallback(
        (ags: string): string | null => regionName(ags),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [geometrien],
    );

    // ── Karte erzeugen (genau einmal) ───────────────────────────────────────
    useEffect(() => {
        if (!containerRef.current || mapRef.current) return;

        const map = L.map(containerRef.current, {
            zoomControl: false,
            attributionControl: false,
            // Die Namensnennung steht in der Legende (CrmMapLegend) — dort ist
            // sie lesbar und kann nicht von einem Panel verdeckt werden.
        }).setView(START_MITTE, START_ZOOM);

        L.control.zoom({ position: 'bottomright' }).addTo(map);
        L.tileLayer(TILE_URL, { maxZoom: 19 }).addTo(map);

        markerLayerRef.current = L.layerGroup().addTo(map);
        mapRef.current = map;

        const merkeAusschnitt = () => {
            const b = map.getBounds();
            setViewport({
                bounds: { west: b.getWest(), south: b.getSouth(), east: b.getEast(), north: b.getNorth() },
                zoom: map.getZoom(),
            });
        };
        map.on('moveend', merkeAusschnitt);
        map.on('zoomend', merkeAusschnitt);
        merkeAusschnitt();

        // Leaflet misst beim Erzeugen die Containergroesse; steht die Ansicht
        // noch im Aufbau, ist sie 0 und die Karte bleibt grau.
        const t = window.setTimeout(() => map.invalidateSize(), 120);

        return () => {
            window.clearTimeout(t);
            map.off();
            map.remove();
            mapRef.current = null;
            markerLayerRef.current = null;
            flaechenLayerRef.current = null;
        };
    }, []);

    // ── Flaechen (Choropleth) ───────────────────────────────────────────────
    const geoAnzeige: GeoCollection | null = useMemo(() => {
        if (!geometrien) return null;
        if (drilldown.ebene === 'land' && drilldown.landAgs) {
            return {
                type: 'FeatureCollection',
                features: geometrien.features.filter(
                    (f) => String(f.properties?.bundesland_ags ?? '') === drilldown.landAgs,
                ),
            };
        }
        return geometrien;
    }, [geometrien, drilldown]);

    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;

        if (flaechenLayerRef.current) {
            flaechenLayerRef.current.remove();
            flaechenLayerRef.current = null;
        }
        if (!layer.choropleth || !geoAnzeige) return;

        const schicht = L.geoJSON(geoAnzeige as any, {
            style: (feature) => {
                const ags = String(feature?.properties?.AGS ?? '');
                const region = regionNachKey.get(ags);
                const wert = region ? metrikWert(region, metrik) : null;
                return {
                    fillColor: choroplethFarbe(skala, wert),
                    fillOpacity: 0.55,
                    color: '#ffffff',
                    weight: 1,
                };
            },
            onEachFeature: (feature, ebene) => {
                const ags = String(feature?.properties?.AGS ?? '');
                const name = String(feature?.properties?.name ?? ags);
                const region = regionNachKey.get(ags);
                const wert = region ? metrikWert(region, metrik) : null;

                ebene.bindTooltip(
                    `<div class="text-[11px]"><strong>${esc(name)}</strong><br/>${esc(METRIK_LABELS[metrik])}: ${
                        wert === null ? 'kein Wert' : esc(formatWert(wert, metrik))
                    }</div>`,
                    { sticky: true },
                );

                ebene.on('mouseover', () => (ebene as L.Path).setStyle({ weight: 2.5, color: '#1e293b' }));
                ebene.on('mouseout', () => (ebene as L.Path).setStyle({ weight: 1, color: '#ffffff' }));
                ebene.on('click', () => {
                    setGewaehltesFeature(null);
                    setClusterListe([]);
                    setGewaehlteRegion(region ?? null);
                    // Bundeslandkarte: Klick zoomt in den Kreis-Drilldown.
                    // Kreiskarte: Klick waehlt nur aus — ein weiterer Drilldown
                    // waere die Gemeindeebene, die es hier nicht gibt.
                    if (drilldown.ebene === 'bund') {
                        zeigeLand(ags);
                        map.fitBounds((ebene as L.Polygon).getBounds(), { padding: [40, 40] });
                    } else {
                        zeigeKreis(ags);
                    }
                });
            },
        });

        schicht.addTo(map);
        // Flaechen gehoeren unter die Marker.
        schicht.bringToBack();
        flaechenLayerRef.current = schicht;
    }, [geoAnzeige, regionNachKey, skala, metrik, layer.choropleth, drilldown.ebene, zeigeLand, zeigeKreis]);

    // ── Marker ──────────────────────────────────────────────────────────────
    const punkte = useMemo(
        () => (viewport ? clusterFeatures(clusterIndex, viewport.bounds, viewport.zoom) : []),
        [clusterIndex, viewport],
    );

    useEffect(() => {
        const map = mapRef.current;
        const gruppe = markerLayerRef.current;
        if (!map || !gruppe) return;

        gruppe.clearLayers();

        for (const p of punkte) {
            if (p.istCluster) {
                const marker = L.marker([p.lat, p.lng], { icon: clusterIcon(p) });
                marker.bindTooltip(`${p.anzahl} Einträge — klicken zum Aufklappen`, { direction: 'top' });
                marker.on('click', () => {
                    const ziel = clusterExpansionZoom(clusterIndex, p.clusterId);
                    // Das Detail-Panel bekommt zusaetzlich die Liste: ein Cluster
                    // muss auch ohne Zoom lesbar sein (Listen-Alternative, Plan 11).
                    setClusterListe(clusterBlaetter(clusterIndex, p.clusterId));
                    setGewaehltesFeature(null);
                    setGewaehlteRegion(null);
                    if (ziel !== null) map.setView([p.lat, p.lng], ziel);
                });
                gruppe.addLayer(marker);
                continue;
            }
            if (!p.feature) continue;
            const f = p.feature;

            // Einsatzradius als Kreis. Er gehoert IN dieselbe LayerGroup wie
            // die Marker: `clearLayers()` raeumt ihn damit beim naechsten
            // Durchlauf mit ab, ohne dass eine zweite Gruppe gepflegt werden
            // muss. Gezeichnet wird er vor dem Marker, damit die Flaeche unter
            // dem Symbol liegt.
            const radiusKm = Number(f.meta?.einsatzradiusKm);
            if (f.kind === 'freelancer' && Number.isFinite(radiusKm) && radiusKm > 0) {
                gruppe.addLayer(
                    L.circle([p.lat, p.lng], {
                        radius: radiusKm * 1000,
                        color: KIND_FARBE.freelancer,
                        weight: 1,
                        fillColor: KIND_FARBE.freelancer,
                        fillOpacity: 0.08,
                        // Der Kreis ist Information, kein Bedienelement — ein
                        // Klick soll den Marker darunter treffen, nicht die
                        // Flaeche, die halb Norddeutschland bedeckt.
                        interactive: false,
                    }),
                );
            }

            const marker = L.marker([p.lat, p.lng], {
                icon: punktIcon(f, istFrischerFund(f)),
                // Ausschreibungen nach oben: ihre Frist laeuft, ein Kunde nicht.
                zIndexOffset: f.kind === 'tender' ? 800 : 400,
            });
            marker.bindTooltip(tooltipHtml(f), { direction: 'top', offset: [0, -14], className: 'leaflet-tooltip-rounded' });
            marker.on('click', () => {
                setClusterListe([]);
                setGewaehlteRegion(null);
                setGewaehltesFeature(f);
            });
            gruppe.addLayer(marker);
        }
    }, [punkte, clusterIndex]);

    // ── Aktionen ────────────────────────────────────────────────────────────

    const springeZuRegion = useCallback(
        (kreisAgs: string) => {
            zeigeKreis(kreisAgs);
            const map = mapRef.current;
            const schicht = flaechenLayerRef.current;
            if (!map || !schicht) return;
            schicht.eachLayer((l: any) => {
                if (String(l.feature?.properties?.AGS ?? '') !== kreisAgs) return;
                if (typeof l.getBounds === 'function') map.fitBounds(l.getBounds(), { padding: [40, 40] });
            });
        },
        [zeigeKreis],
    );

    const ebenenTitel =
        drilldown.ebene === 'bund'
            ? 'Deutschland'
            : drilldown.ebene === 'land'
              ? `Bundesland ${nameFuer(drilldown.landAgs || '') ?? drilldown.landAgs}`
              : `Kreis ${nameFuer(drilldown.kreisAgs || '') ?? drilldown.kreisAgs}`;

    return (
        <div data-glass-view="crm-map" className="relative w-full h-full min-h-[480px] overflow-hidden">
            <style>{MARKER_CSS}</style>

            <div ref={containerRef} className="absolute inset-0 z-0" aria-label="CRM-Karte Deutschland" role="application" />

            {/* ── Filterleiste oben ── */}
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[400] w-[min(96%,900px)] pointer-events-none">
                <div className="glass rounded-2xl px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-2 pointer-events-auto">
                    <span className="flex items-center gap-1.5 text-xs font-bold text-slate-700 shrink-0">
                        <MapIcon className="w-4 h-4 text-blue-500" /> CRM-Karte
                    </span>

                    {drilldown.ebene !== 'bund' && (
                        <button
                            type="button"
                            onClick={zeigeBund}
                            className="glass-button rounded-lg px-2 py-1 text-[11px] font-bold text-slate-600 flex items-center gap-1"
                        >
                            <ChevronLeft className="w-3 h-3" /> Deutschland
                        </button>
                    )}

                    <div className="flex items-center gap-1 flex-wrap">
                        {CRM_MAP_LAYERS.map((key: CrmMapLayer) => {
                            const info = LAYER_INFO[key];
                            const an = layer[key];
                            return (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => toggleLayer(key)}
                                    disabled={info.baldVerfuegbar}
                                    aria-pressed={an}
                                    title={info.hinweis}
                                    className={`px-2 py-1 rounded-lg text-[11px] font-bold border transition-colors ${
                                        info.baldVerfuegbar
                                            ? 'border-dashed border-slate-300 text-slate-400 cursor-not-allowed'
                                            : an
                                              ? 'bg-blue-500/10 border-blue-200 text-blue-700'
                                              : 'border-transparent text-slate-500 hover:bg-white/70'
                                    }`}
                                >
                                    {info.label}
                                    {info.baldVerfuegbar && <span className="ml-1 font-normal">(bald)</span>}
                                </button>
                            );
                        })}
                    </div>

                    <label className="flex items-center gap-1 text-[11px] text-slate-600 ml-auto">
                        <span className="sr-only">Metrik</span>
                        <select
                            value={metrik}
                            onChange={(e) => setMetrik(e.target.value as CrmMapMetrik)}
                            className="bg-white/70 border border-white/70 rounded-lg px-2 py-1 text-[11px] font-bold text-slate-700"
                        >
                            {(Object.keys(METRIK_LABELS) as CrmMapMetrik[]).map((m) => (
                                <option key={m} value={m}>
                                    {METRIK_LABELS[m]}
                                </option>
                            ))}
                        </select>
                    </label>

                    <label className="flex items-center gap-1 text-[11px] text-slate-600">
                        <span className="sr-only">Zeitraum</span>
                        <select
                            value={monate}
                            onChange={(e) => setMonate(Number(e.target.value))}
                            title={`${zeitraum.von} bis ${zeitraum.bis}`}
                            className="bg-white/70 border border-white/70 rounded-lg px-2 py-1 text-[11px] font-bold text-slate-700"
                        >
                            <option value={3}>3 Monate</option>
                            <option value={6}>6 Monate</option>
                            <option value={12}>12 Monate</option>
                            <option value={24}>24 Monate</option>
                        </select>
                    </label>

                    <button
                        type="button"
                        onClick={neuLaden}
                        title="Daten neu laden"
                        className="glass-button rounded-lg p-1.5 text-slate-600"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${laedt ? 'animate-spin' : ''}`} />
                    </button>
                </div>

                {fehler && (
                    <div className="glass rounded-xl mt-2 px-3 py-2 text-[11px] text-rose-700 flex items-start gap-1.5 pointer-events-auto">
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
                        <span>{fehler}</span>
                    </div>
                )}
            </div>

            {/* ── KPI-Panel links ── */}
            <div className="absolute top-20 left-3 z-[400] w-72 max-h-[calc(100%-6rem)] overflow-y-auto no-scrollbar pointer-events-auto">
                <CrmMapKpiPanel
                    regionen={regionen}
                    metrik={metrik}
                    branchen={branchen}
                    brancheFilter={brancheFilter}
                    onBrancheFilter={setBrancheFilter}
                    weisseFlecken={weisseFlecken}
                    nameFuer={nameFuer}
                    onZoomSpot={(s) => springeZuRegion(s.key)}
                    onSelectRegion={(r) => {
                        setGewaehltesFeature(null);
                        setClusterListe([]);
                        setGewaehlteRegion(r);
                        if (!r.unbekannt && r.level === 'kreis') springeZuRegion(r.key);
                        else if (!r.unbekannt) zeigeLand(r.key);
                    }}
                    aktiverRegionKey={gewaehlteRegion?.key}
                    ebenenTitel={ebenenTitel}
                />
            </div>

            {/* ── Detail rechts ── */}
            <div className="absolute top-20 right-3 z-[400] pointer-events-auto">
                <CrmMapDetailPanel
                    feature={gewaehltesFeature}
                    region={gewaehlteRegion}
                    regionName={gewaehlteRegion ? nameFuer(gewaehlteRegion.key) : null}
                    metrik={metrik}
                    clusterFeatures={clusterListe}
                    onSelectFeature={(f) => {
                        setClusterListe([]);
                        setGewaehltesFeature(f);
                    }}
                    onClose={() => {
                        setGewaehltesFeature(null);
                        setGewaehlteRegion(null);
                        setClusterListe([]);
                    }}
                    onOpenCustomer={kundenakteOffen ? onOpenCustomer : undefined}
                    onOpenProject={onOpenProject}
                />
            </div>

            {/* ── Legende + Attribution unten links ── */}
            <div className="absolute bottom-3 left-3 z-[400] pointer-events-none">
                <CrmMapLegend
                    scale={skala}
                    metrik={metrik}
                    aggregateFehlen={aggregateFehlen}
                    sichtbar={layer.choropleth}
                />
            </div>

            {/* ── Statuszeile unten rechts ── */}
            <div className="absolute bottom-3 right-16 z-[400] pointer-events-none">
                <span className="glass rounded-full px-3 py-1 text-[11px] font-bold text-slate-600 flex items-center gap-1.5">
                    {(laedt || geometrienLaden) && <Loader2 className="w-3 h-3 animate-spin" />}
                    {sichtbareFeatures.length} Marker · {regionen.length} Regionen
                </span>
            </div>
        </div>
    );
};

/** Wie formatWert im Hook, aber ohne Import-Zyklus ueber die Panels. */
const formatWert = (n: number, metrik: CrmMapMetrik): string => {
    if (metrik === 'umsatz') {
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR',
            maximumFractionDigits: 0,
        }).format(n / 100);
    }
    if (metrik === 'conversion') return `${Math.round(n)} %`;
    return String(Math.round(n));
};

/**
 * Marker-CSS. Bewusst hier und nicht in index.css: die Regeln gehoeren
 * ausschliesslich zu diesen DivIcons, und wer die Karte entfernt, entfernt sie
 * mit. Tailwind kann hier nichts ausrichten — Leaflet fuegt das HTML selbst
 * ein, und die Puls-Animation braucht eine benannte Keyframe-Regel.
 */
const MARKER_CSS = `
.crm-map-icon { background: none; border: none; }
.crm-map-wrap { position: relative; display: block; width: 100%; height: 100%; }
.crm-map-wrap svg { display: block; }

/* Puls fuer Scraper-Funde der letzten 7 Tage (Plan Abschnitt 6). */
.crm-map-puls {
  position: absolute; inset: -6px;
  border-radius: 9999px;
  background: var(--puls-farbe, #60a5fa);
  opacity: .45;
  animation: crm-map-puls 1.8s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  pointer-events: none;
}
@keyframes crm-map-puls {
  0%   { transform: scale(.6); opacity: .5; }
  70%  { transform: scale(1.5); opacity: 0; }
  100% { transform: scale(1.5); opacity: 0; }
}
@media (prefers-reduced-motion: reduce) {
  /* Wer Bewegung abbestellt hat, bekommt einen Ring statt einer Animation —
     die Information „frischer Fund" bleibt, das Flackern verschwindet. */
  .crm-map-puls { animation: none; opacity: .3; transform: scale(1.2); }
}

/* Frist-Countdown am Ausschreibungs-Fadenkreuz. */
.crm-map-frist {
  position: absolute; top: -8px; right: -12px;
  background: #475569; color: #fff;
  font-size: 9px; font-weight: 800; line-height: 1;
  padding: 2px 4px; border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0,0,0,.25);
}
.crm-map-frist-eng { background: #e11d48; }
.crm-map-frist-ab  { background: #94a3b8; }

/* Cluster-Blase. */
.crm-map-cluster {
  display: flex; align-items: center; justify-content: center;
  border-radius: 9999px; color: #fff;
  font-weight: 800; font-size: 11px;
  border: 2.5px solid rgba(255,255,255,.9);
  box-shadow: 0 2px 8px rgba(0,0,0,.2);
}
`;
